<template>
  <div v-if="newGroupChat" class="w-100 d-flex flex-column">
    <proa-heading>{{ $t('pages.chat.new.heading') }}</proa-heading>
    <proa-card
      :class="['new-group-chat-card', {
        'disabled': createGroupChatRequesting
      }]"
    >
      <template v-if="!reachedMaxGroups">
        <table class="table-attributes">
          <tbody>
            <tr>
              <th>{{ $t('models.groupChat.attributes.groupChatName') }}</th>
              <td>
                <span
                  :class="['attribute-wrapper', { 'invalid': validateErrors && validateErrors.groupChatName }]"
                  @click="editGroupChatNameModal = true; validateErrors = null"
                >
                  <proa-error-badge v-if="validateErrors && validateErrors.groupChatName"/>
                  <img :src="require('@/assets/images/icons/edit.png')"/>
                  <span :class="['attribute', { 'empty': !newGroupChat.groupChatName }]">{{ newGroupChat.groupChatName || $t('models.groupChat.placeHolders.groupChatName') }}</span>
                </span>
              </td>
            </tr>
            <tr>
              <th valign="top">{{ $t('models.groupChat.attributes.groupChatDescription') }}</th>
              <td>
                <span class="attribute-wrapper" @click="editGroupChatDescriptionModal = true">
                  <img :src="require('@/assets/images/icons/edit.png')"/>
                  <span
                    v-html="newGroupChat.groupChatDescription ? $options.filters.autoLink(newGroupChat.groupChatDescription) : $t('models.groupChat.placeHolders.groupChatDescription')"
                    :class="['attribute text-pre-wrap', { 'empty': !newGroupChat.groupChatDescription }]"
                  />
                </span>
              </td>
            </tr>
            <tr v-if="enableSelectGroupChatType">
              <th>{{ $t('models.groupChat.attributes.groupChatType') }}</th>
              <td>
                <proa-radio-button
                  :checked="newGroupChat.groupChatType == 'closedChat'"
                  @change="onChangeGroupChatType('closedChat')"
                  class="mr-3"
                >
                  <span>{{ $t('models.groupChat.groupChatTypes.closedChat') }}</span>
                </proa-radio-button>
                <proa-radio-button
                  :checked="newGroupChat.groupChatType == 'publicChat'"
                  @change="onChangeGroupChatType('publicChat')"
                >
                  <span>{{ $t('models.groupChat.groupChatTypes.publicChat') }}</span>
                </proa-radio-button>
                <div class="mt-2 d-none d-md-block">{{ $t('models.groupChat.groupChatTypeRestrictions') }}</div>
              </td>
            </tr>
            <tr v-if="newGroupChat.groupChatType == 'closedChat'">
              <th>{{ $t('models.groupChat.users') }}（{{ newGroupChat.userIds.length }}{{ $t('common.person') }}）</th>
              <td>
                <proa-button
                  @click.native="addGroupChatMembersModal = true"
                  class="default-button-width mr-2"
                >
                  {{ $t('common.addMember') }}
                </proa-button>
                <proa-button
                  :disabled="newGroupChat.userIds.length == 1"
                  @click.native="deleteGroupChatMembersModal = true"
                  class="default-button-width"
                >
                  {{ $t('common.deleteMember') }}
                </proa-button>
              </td>
            </tr>
            <tr v-else-if="newGroupChat.groupChatType == 'publicChat'">
              <th>{{ $t('models.groupChat.users') }}</th>
              <td>{{ $t('models.groupChat.publicChatExplanation') }}</td>
            </tr>
          </tbody>
        </table>
        <div class="members">
          <div v-if="newGroupChat.groupChatType == 'closedChat'" class="members-scroll-wrapper">
            <div v-for="user in addedUsers" :key="user.id" class="member">
              <proa-avatar :src="user.meta.avatar || require('@/assets/images/avatar_default.png')" size="sm"/>
              <span class="ml-2 pl-1">{{ user.meta.displayName }}</span>
            </div>
          </div>
        </div>
      </template>
      <div v-else class="disabled-messsage">
        <proa-heading sub>{{ $t('pages.chat.new.reachedMaxGroupsInOrganization') }}</proa-heading>
      </div>
      <div class="new-group-chat-card-footer">
        <proa-error-box
          v-if="validateErrors"
          :message="Object.values(validateErrors)[0]"
          class="mr-2"
        />
        <proa-button
           @click.native="$router.push({ name: 'chat' })"
           class="mr-2 default-button-width"
         >
           {{ $t('common.cancel') }}
         </proa-button>
         <proa-button
           v-if="!reachedMaxGroups"
           :disabled="!validated || validateErrors != null"
           :loading="createGroupChatRequesting"
           variant="primary"
           class="default-button-width"
           @click.native="onClickCreateGroupChat"
         >
           {{ $t('common.create') }}
         </proa-button>
      </div>
    </proa-card>
    <validation-observer v-slot="{ invalid }">
      <proa-modal
        :modal="editGroupChatNameModal"
        :title="$t('models.groupChat.attributes.groupChatName')"
        @close="closeEditGroupChatNameModal(false)"
      >
        <validation-provider
          :name="$t('models.groupChat.attributes.groupChatName')"
          rules="max:50"
          v-slot="{ errors }"
        >
          <proa-input-group :invalidFeedback="errors[0]">
            <template slot="input">
              <proa-input
                v-model="newGroupChatInner.groupChatName"
                :invalid="errors.length != 0"
                type="text"
                dark
              />
            </template>
          </proa-input-group>
        </validation-provider>
        <template slot="footer">
          <proa-button
            @click.native="closeEditGroupChatNameModal(false)"
            class="default-button-width mr-2"
          >
            {{ $t('common.cancel') }}
          </proa-button>
          <proa-button
            @click.native="closeEditGroupChatNameModal(true)"
            :disabled="invalid"
            class="default-button-width"
          >
            {{ $t('common.ok') }}
          </proa-button>
        </template>
      </proa-modal>
    </validation-observer>
    <proa-modal
      :modal="editGroupChatDescriptionModal"
      :title="$t('models.groupChat.attributes.groupChatDescription')"
      @close="closeEditGroupChatDescriptionModal(false)"
    >
      <proa-input
        v-model="newGroupChatInner.groupChatDescription"
        type="textarea"
        dark
      />
      <template slot="footer">
        <proa-button @click.native="closeEditGroupChatDescriptionModal(false)" class="default-button-width mr-2">
          {{ $t('common.cancel') }}
        </proa-button>
        <proa-button
          class="default-button-width"
          @click.native="closeEditGroupChatDescriptionModal(true)"
        >
          {{ $t('common.ok') }}
        </proa-button>
      </template>
    </proa-modal>
    <select-member-modal
      :modal="addGroupChatMembersModal"
      :userIds="newGroupChat.userIds"
      type="add"
      @close="addGroupChatMembersModal = false"
      @confirm="addUserIds"
    />
    <select-member-modal
      :modal="deleteGroupChatMembersModal"
      :userIds="newGroupChat.userIds"
      type="delete"
      @close="deleteGroupChatMembersModal = false"
      @confirm="deleteUserIds"
    />
    <proa-modal
      :modal="createdModalWithId != null"
      :title="$t('models.groupChat.created')"
      @close="closeCreatedModal"
    >
      {{ $t('pages.chat.new.createdMessage') }}
    </proa-modal>
  </div>
</template>

<script>
import i18n from '@/i18n'
import { mapGetters, mapActions } from 'vuex'
import GroupChat from '@/store/models/GroupChat'
import Role from '@/store/models/Role'
import User from '@/store/models/User'
import Organization from '@/store/models/Organization'

import SelectMemberModal from '@/components/organisms/SelectMemberModal'

export default {
  name: 'NewChat',
  components: {
    SelectMemberModal
  },
  computed: {
    reachedMaxGroups: function() {
      return this.currentLicensedPlan.maxGroups !== null && this.currentLicensedPlan.maxGroups <= this.currentOrganization.currentGroups
    },
    validated: function() {
      return this.newGroupChat.groupChatName
    },
    currentOrganizationRole: function() {
      return Role.find(this.signedInUser.currentOrganizationRoleId)
    },
    enableSelectGroupChatType: function() {
      return this.currentOrganizationRole && this.currentOrganizationRole.roleType === 'officeMaster'
    },
    addedUsers: function() {
      return User.findIn(this.newGroupChat.userIds)
    },
    ...mapGetters({
      httpError: 'httpError',
      signedInUser: 'auth/signedInUser',
      currentOrganization: 'auth/currentOrganization',
      currentLicensedPlan: 'auth/currentLicensedPlan'
    })
  },
  data() {
    return {
      newGroupChat: null,
      newGroupChatInner: {
        groupChatName: '',
        groupChatDescription: ''
      },
      validateErrors: null,
      editGroupChatNameModal: false,
      editGroupChatDescriptionModal: false,
      addGroupChatMembersModal: false,
      deleteGroupChatMembersModal: false,
      createGroupChatRequesting: false,
      createdModalWithId: null
    }
  },
  methods: {
    onChangeGroupChatType: function(groupChatType) {
      if (groupChatType === 'publicChat') {
        this.newGroupChat.userIds = []
      } else if (groupChatType === 'closedChat') {
        this.newGroupChat.userIds = [this.signedInUser.id]
      }
      this.newGroupChat.groupChatType = groupChatType
    },
    closeEditGroupChatNameModal: function(accept = false) {
      if (accept) {
        this.newGroupChat.groupChatName = this.newGroupChatInner.groupChatName
      } else {
        this.newGroupChatInner.groupChatName = this.newGroupChat.groupChatName
      }
      this.editGroupChatNameModal = false
    },
    closeEditGroupChatDescriptionModal: function(accept = false) {
      if (accept) {
        this.newGroupChat.groupChatDescription = this.newGroupChatInner.groupChatDescription
      } else {
        this.newGroupChatInner.groupChatDescription = this.newGroupChat.groupChatDescription
      }
      this.editGroupChatDescriptionModal = false
    },
    closeCreatedModal: function() {
      this.$router.push({ name: 'showChat', params: { id: this.createdModalWithId } })
    },
    addUserIds: function({ userIds }) {
      this.addGroupChatMembersModal = false
      this.newGroupChat.userIds = this.newGroupChat.userIds.concat(userIds)
    },
    deleteUserIds: function({ userIds }) {
      this.deleteGroupChatMembersModal = false
      this.newGroupChat.userIds = this.newGroupChat.userIds.filter(userId => !userIds.includes(userId))
    },
    async onClickCreateGroupChat() {
      this.createGroupChatRequesting = true
      const createdGroupChatId = await this.apiPostGroupChat(this.newGroupChat)
      this.createGroupChatRequesting = false

      if (createdGroupChatId) {
        this.createdModalWithId = createdGroupChatId
        Organization.update({
          id: this.currentOrganization.id,
          currentGroups: this.currentOrganization.currentGroups + 1
        })
      } else {
        if (this.httpError?.response?.data?.errors) {
          this.validateErrors = Object.fromEntries(
            Object.entries(this.httpError.response.data.errors).map((array) => {
              return [
                array[0],
                i18n.tc(`models.groupChat.attributes.${array[0]}`) + array[1][0]
              ]
            })
          )
        }
      }
    },
    ...mapActions({
      apiPostGroupChat: 'entities/groupChats/create',
    })
  },
  created() {
    if (!this.signedInUser.creatable) {
      this.$router.push({ name: 'chat' })
      return
    }
    this.newGroupChat = GroupChat.skeleton()
    this.newGroupChat.groupChatType = 'closedChat'
    this.newGroupChat.groupChatOwnerId = this.signedInUser.id
    this.newGroupChat.userIds = [this.signedInUser.id]
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables";

.new-group-chat-card {
  display: flex;
  flex-direction: column;
  margin-top: 40px;
  flex-grow: 1;

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  .disabled-messsage {
    flex-grow: 1;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .table-attributes {
    width: 100%;

    tr:not(:last-child) {
      border-bottom: 1px solid $dark-border-color;
    }

    th {
      width: 180px;
      white-space: nowrap;
      padding: 20px 0 20px 20px;
      font-weight: 600;
    }

    tr:last-child th {
      padding: 40px 0 40px 20px;
    }

    td {
      padding: 20px 0;
      img {
        width: 20px;
        height: 20px;
      }

      .attribute-wrapper {
        display: flex;
        cursor: pointer;

        &.invalid .attribute {
          color: $error-color;
        }
      }

      .attribute {
        margin-left: 10px;

        &.empty {
          color: $link-color;
        }
      }
    }
  }

  .title {
    font-weight: 600;
  }

  .members {
    flex-grow: 1;
    position: relative;

    .members-scroll-wrapper {
      position: absolute;
      height: 100%;
      width: 100%;
      padding-left: 20px;
      overflow-y: auto;
      display: flex;
      flex-direction: column;

      .member {
        margin-bottom: 10px;

        &:last-child::after {
          content: '';
          display: block;
          height: 20px;
        }
      }
    }
  }

  .new-group-chat-card-footer {
    display: flex;
    justify-content: flex-end;
    padding: 20px;
    border-top: 1px solid $dark-border-color;
  }
}
</style>
